import Badge from 'components/styled/badge'
import { formatCurrency } from 'lib/formatting/currency'
import { formatDate } from 'lib/formatting/dates'
import {
  FuneralPlanAgreementQuoteLine,
  FuneralPlanAgreementStatus,
  FuneralPlanPayments,
  FuneralPlanVersion,
} from 'lib/models/funeral-plan'

/** Helpers and small pieces shared by the parts of the payment page (2.14). */

export const formatPence = (valueInPence: number): string =>
  formatCurrency({ showPence: true, value: valueInPence, valueInPence: true })

export const formatDay = (date: string): string =>
  formatDate(date, false, 'D MMMM YYYY') ?? ''

/** A date short enough not to wrap in a table column, e.g. "29 Aug 2026". */
export const formatShortDay = (date: string): string => formatDate(date) ?? ''

/** The day of the month payments fall on, e.g. "29th of each month". */
export const formatPaymentDay = (startsOn: string): string =>
  `${formatDate(startsOn, false, 'Do')} of each month`

/** When an agreement's payments fall: one date, or a day each month. */
export const formatPaymentDate = ({
  count,
  startsOn,
}: FuneralPlanPayments): string =>
  count > 1
    ? `${formatPaymentDay(startsOn)}, from ${formatDay(startsOn)}`
    : formatDay(startsOn)

/** An agreement in any of these is not, or no longer, part of the plan. */
const AGREEMENT_STATUS_BADGE: Partial<
  Record<FuneralPlanAgreementStatus, string>
> = {
  error: 'Error',
  cancelled: 'Cancelled',
}

/**
 * The badge an agreement carries, if any: its status when it is not part of
 * the plan, or "Part paid" for something paid in one go that is still owed. The
 * error state for a short-paid agreement is still to be agreed with design.
 */
export const agreementBadge = ({
  type,
  agreement,
  position,
}: FuneralPlanVersion): {
  label: string
  state: 'info' | 'error'
} | null => {
  const label = AGREEMENT_STATUS_BADGE[agreement.status]
  if (label) return { label, state: 'info' }
  if (type === 'Upfront' && position.outstandingInPence > 0)
    return { label: 'Part paid', state: 'error' }
  return null
}

const CHANGE_BADGES = {
  added: { label: 'Added', state: 'success' },
  removed: { label: 'Removed', state: 'error' },
  changed: { label: 'Changed', state: 'info' },
} as const

/** What an agreement did with a line: Added in green, Removed in red,
 * Changed in blue. */
export const ChangeBadge = ({
  change,
  className,
}: {
  change: FuneralPlanAgreementQuoteLine['change']
  className?: string
}): React.ReactElement | null =>
  change && (
    <Badge className={className} $state={CHANGE_BADGES[change].state}>
      {CHANGE_BADGES[change].label}
    </Badge>
  )
